import { request, uploadToStorage } from './client.js';

/**
 * Typed calls against the VoxFauna API.
 *
 * These shapes mirror the OpenAPI document at `/openapi.json`. If you are
 * adding an endpoint, read the spec rather than guessing — it is generated from
 * the controllers, so it cannot be out of date.
 */

// ── Identity ────────────────────────────────────────────────────────────────

export interface Contributor {
  id: string;
  email: string | null;
  displayName: string | null;
  defaultRole: string;
  experienceYearsBySpecies: Record<string, number>;
  reputationScore: number | null;
  createdAt: string;
}

export const getMe = () => request<Contributor>('/v1/me');

// ── Consent ─────────────────────────────────────────────────────────────────

export interface Terms {
  version: string;
  licence: string;
  checksum: string;
  body: string;
}

export interface ConsentStatus {
  hasAccepted: boolean;
  isCurrent: boolean;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  currentVersion: string;
  licence: string;
}

export const getTerms = () => request<Terms>('/v1/consent/terms', { anonymous: true });
export const getConsentStatus = () => request<ConsentStatus>('/v1/consent/status');
export const acceptTerms = (termsVersion: string) =>
  request<{ acceptedAt: string; termsVersion: string; licence: string }>('/v1/consent/accept', {
    method: 'POST',
    body: { termsVersion },
  });

// ── Subjects ────────────────────────────────────────────────────────────────

export interface Subject {
  id: string;
  name: string | null;
  scientificName: string;
  commonName: string | null;
  breedOrMorph: string | null;
  /**
   * The canonical breed, when what was typed matched a published list.
   * Derived server-side and read-only here — never sent on create.
   */
  breedCode: string | null;
  sex: 'male' | 'female' | 'unknown' | null;
  birthDate: string | null;
  lifeStage: string | null;
  captiveStatus: string | null;
  createdAt: string;
}

export const listSubjects = () =>
  request<{ items: Subject[] }>('/v1/subjects').then((r) => r.items);

export const createSubject = (input: Partial<Subject> & { scientificName: string }) =>
  request<Subject>('/v1/subjects', { method: 'POST', body: input });

// ── Ethograms ───────────────────────────────────────────────────────────────

export type EthogramCategory =
  | 'signal_types'
  | 'body_language'
  | 'antecedent_terms'
  | 'consequence_terms'
  | 'meaning_categories';

/** A category maps term → definition. `$comment` keys are documentation. */
export type TermMap = Record<string, string>;

export interface Ethogram {
  ethogram_version: string;
  taxon: string;
  signal_types: TermMap;
  body_language: TermMap;
  antecedent_terms: TermMap;
  consequence_terms: TermMap;
  meaning_categories: TermMap;
}

export interface EthogramTerm {
  term: string;
  label: string;
  definition: string;
}

/**
 * Fetches the current vocabulary for a species.
 *
 * **Never hardcode these terms.** They are provisional, they will be corrected
 * by people who know more than we do, and the entire point of serving them is
 * that a correction reaches contributors without an app release. Every record
 * stores the `ethogram_version` it was coded against, so a revision never
 * invalidates existing data.
 */
export const getLatestEthogram = (scientificName: string) =>
  request<Ethogram>(
    `/v1/ethograms/latest?scientificName=${encodeURIComponent(scientificName)}`,
    { anonymous: true },
  );

// ── Breed lists ─────────────────────────────────────────────────────────────

export interface BreedList {
  breed_list_version: string;
  taxon: string;
  /** Canonical breeds, already in the order they should be offered. */
  breeds: string[];
}

/**
 * Suggestions for the breed field, per species.
 *
 * Suggestions only — the field accepts anything. A contributor whose dog is a
 * rare breed, a mix, or simply unknown must be able to say so, so a failure
 * here is not an error worth showing: the field degrades to plain text and the
 * animal is added exactly the same way.
 */
export const getLatestBreedList = (scientificName: string) =>
  request<BreedList>(
    `/v1/breeds/latest?scientificName=${encodeURIComponent(scientificName)}`,
    { anonymous: true },
  );

/**
 * Turns a raw category map into renderable options.
 *
 * Labels are derived from the term rather than stored, because FaunaCore
 * ethograms carry definitions but not display names. `stimulus.vehicle_approach`
 * becomes "Vehicle approach" — good enough to render, and the definition is
 * shown alongside so the contributor is never guessing what a term means.
 */
export function toTerms(map: TermMap | undefined): EthogramTerm[] {
  if (!map) return [];
  return Object.entries(map)
    .filter(([term]) => !term.startsWith('$'))
    .map(([term, definition]) => ({ term, label: humanise(term), definition }));
}

function humanise(term: string): string {
  const tail = term.split('.').slice(1).join(' ') || term;
  const words = tail.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ── Media ───────────────────────────────────────────────────────────────────

export interface PresignedUpload {
  assetId: string;
  url: string;
  headers: Record<string, string>;
  expiresAt: string;
  lossless: boolean;
}

export const requestUpload = (input: {
  contentType: string;
  sizeBytes: number;
  checksumSha256: string;
}) => request<PresignedUpload>('/v1/media/uploads', { method: 'POST', body: input });

export const confirmUpload = (assetId: string, durationMs: number) =>
  request<{ assetId: string; state: string; durationMs: number | null }>(
    `/v1/media/${assetId}/confirm`,
    { method: 'POST', body: { durationMs } },
  );

export const getPlaybackUrl = (assetId: string) =>
  request<{ url: string }>(`/v1/media/${assetId}/playback`).then((r) => r.url);

export { uploadToStorage };

// ── Observations ────────────────────────────────────────────────────────────

export interface ObservationSummary {
  id: string;
  schemaVersion: string;
  ethogramVersion: string;
  verificationStatus: string;
  supersededBy: string | null;
  record: Record<string, unknown>;
  createdAt: string;
}

export const submitObservation = (observation: Record<string, unknown>) =>
  request<ObservationSummary>('/v1/observations', { method: 'POST', body: { observation } });

export const listObservations = (params: { subjectId?: string; cursor?: string; limit?: number }) => {
  const query = new URLSearchParams();
  if (params.subjectId) query.set('subjectId', params.subjectId);
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.limit) query.set('limit', String(params.limit));
  const suffix = query.toString();
  return request<{ items: ObservationSummary[]; nextCursor: string | null }>(
    `/v1/observations${suffix ? `?${suffix}` : ''}`,
  );
};

export const getObservation = (id: string) =>
  request<ObservationSummary>(`/v1/observations/${id}`);

export const withdrawObservation = (id: string) =>
  request<void>(`/v1/observations/${id}`, { method: 'DELETE' });
